import { HostedFieldsHostedFieldsFieldData, HostedFieldsState } from 'braintree-web/hosted-fields';

import {
  BraintreeEventTypes,
  TrafficNotification,
  braintreeTracking,
  registrationDataTrafficTracker,
} from '@services/diiService';
import { ContainerType } from '@types-internal';
import { FieldName, FlagByFieldName, TokenizeError } from '@types-internal/BraintreeTypes';
import { safeStringify } from '@utils/utils';

export function isTokenizeError(value: any): value is TokenizeError {
  return (
    value &&
    value instanceof Object &&
    // eslint-disable-next-line no-prototype-builtins
    value.hasOwnProperty('code') &&
    typeof value.code === 'string'
  );
}

export function getInvalidFieldKeys(tokenizeError: TokenizeError) {
  const maybeInvalidFieldKeys = tokenizeError.details?.invalidFieldKeys;
  return maybeInvalidFieldKeys && Array.isArray(maybeInvalidFieldKeys) ? maybeInvalidFieldKeys : [];
}

export function getMessage(tokenizeError: TokenizeError) {
  return tokenizeError.message && typeof tokenizeError.message === 'string'
    ? tokenizeError.message
    : '';
}

export function maybeBraintreeTracking(
  containerType: ContainerType,
  eventType: BraintreeEventTypes,
) {
  if (containerType === 'registration' || containerType === 'default') {
    braintreeTracking(eventType);
  }
}

export function maybeTrafficNotificationError(
  field: string,
  value: HostedFieldsHostedFieldsFieldData,
  isSupported: boolean,
) {
  if (field === 'number' && !isSupported) {
    return 'Unsupported card number';
  } else if (!value.isValid) {
    return 'Invalid value';
  } else if (value.isEmpty) {
    return 'Empty value';
  } else {
    return null;
  }
}

const FIELD_NAMES = ['number', 'cardholderName', 'expirationMonth', 'expirationYear', 'cvv'];

export function isFieldName(value: string): value is FieldName {
  return FIELD_NAMES.indexOf(value) !== -1;
}

export function getDiiEventName(containerType: ContainerType) {
  switch (containerType) {
    case 'registration':
      return 'checkout_registerBT';
    case 'myaccount':
      return undefined;
    default:
      return 'edit_paymentBT';
  }
}

export function maybeTrackErrors(
  containerType: ContainerType,
  trafficNotifications: TrafficNotification[],
) {
  const maybeDiiEventName = getDiiEventName(containerType);

  if (maybeDiiEventName) {
    const { setTrackingErrors } = registrationDataTrafficTracker();
    setTrackingErrors(maybeDiiEventName, trafficNotifications);
  }
}

export function makeTrafficNotifications(tokenizeError: unknown) {
  let trafficNotifications: TrafficNotification[];

  if (isTokenizeError(tokenizeError)) {
    switch (tokenizeError.code) {
      case 'HOSTED_FIELDS_FIELDS_EMPTY':
        // All fields are empty
        trafficNotifications = FIELD_NAMES.map((field) => {
          return { type: 'frontError', field, error: 'Empty value' };
        });
        break;
      case 'HOSTED_FIELDS_FIELDS_INVALID':
        // Some fields are invalid
        trafficNotifications = getInvalidFieldKeys(tokenizeError).map((field) => {
          return { type: 'frontError', field, error: 'Invalid value' };
        });
        break;
      case 'HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE':
        // occurs when:
        //   * the client token used for client authorization was generated
        //     with a customer ID and the fail on duplicate payment method
        //     option is set to true
        //   * the card being tokenized has previously been vaulted (with any customer)
        // See: https://developer.paypal.com/braintree/docs/reference/request/client-token/generate#options.fail_on_duplicate_payment_method
        trafficNotifications = [
          {
            type: 'frontError',
            field: '',
            error: "The payment method already exists in the customer's vault",
          },
        ];
        break;
      case 'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED':
        // occurs when:
        //   * the client token used for client authorization was generated
        //     with a customer ID and the verify card option is set to true
        //     and you have credit card verification turned on in the Braintree
        //     control panel
        //   * the cvv does not pass verification (https://developer.paypal.com/braintree/docs/reference/general/testing#avs-and-cvv/cid-responses)
        // See: https://developer.paypal.com/braintree/docs/reference/request/client-token/generate#options.verify_card
        trafficNotifications = [
          {
            type: 'frontError',
            field: '',
            error: 'CVV did not pass verification',
          },
        ];
        break;
      case 'HOSTED_FIELDS_FAILED_TOKENIZATION':
        // occurs for any other tokenization error on the server
        trafficNotifications = [
          {
            type: 'frontError',
            field: '',
            error: `Tokenization failed server side with error: ${getMessage(tokenizeError)}`,
          },
        ];
        break;
      case 'HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR':
        // occurs when the Braintree gateway cannot be contacted
        trafficNotifications = [
          {
            type: 'frontError',
            field: '',
            error: `Network error occurred when tokenizing with error: ${getMessage(
              tokenizeError,
            )}`,
          },
        ];
        break;
      default:
        trafficNotifications = [
          {
            type: 'frontError',
            field: '',
            error: `Something bad happened when tokenizing, error: ${getMessage(tokenizeError)}`,
          },
        ];
    }
  } else {
    trafficNotifications = [
      {
        type: 'frontError',
        field: '',
        error: `Something bad happened when tokenizing, general error: ${safeStringify(
          tokenizeError,
        )}`,
      },
    ];
  }

  return trafficNotifications;
}

export function gatherDataByFieldName(state: HostedFieldsState, isSupported: boolean) {
  const validityByFieldName: FlagByFieldName = {},
    emptinessByFieldName: FlagByFieldName = {},
    trafficNotifications: TrafficNotification[] = [];

  Object.entries(state.fields).forEach(([field, value]) => {
    if (isFieldName(field)) {
      const error = maybeTrafficNotificationError(field, value, isSupported);
      if (error) {
        trafficNotifications.push({ type: 'frontError', field, error });
      }

      validityByFieldName[field] = value.isValid;
      emptinessByFieldName[field] = value.isEmpty;
    }
  });

  return { validityByFieldName, emptinessByFieldName, trafficNotifications };
}
